import * as React from 'react';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import { TimeConstraints } from 'react-datetime';
import PhoneInput from 'react-phone-number-input/input';
import debounce from 'lodash/debounce';
import moment from 'moment';

import { DatePicker, Filter, PicklistWithHandler, RadioButton, Select } from './';

import 'react-phone-number-input/style.css';
import './style.scss';

interface IField extends ICustomFieldBase {
  type: CustomFieldType | LocationFieldType | OtherFieldType | 'password' | 'color' | string;
  primaryColor?: ButtonStyle;
  secondaryColor?: ButtonStyle;
  showHandler?: boolean;
  labelType?: LabelType;
  isResizeable?: boolean;
}

interface IInputProps {
  field: IField;
  name: string;
  renderLabel?: () => any;
  width: number;
  widthMD?: number;
  placeholder?: string;
  valueLabelOptions?: Array<{ value: any; label: string; disabled?: boolean }>;
  onChange: (event: any) => void;
  onOptionSelection?: (event: any) => void;
  onBlur?: (event: any) => void;
  onFocus?: (event: any) => void;
  className?: string;
  disabled?: boolean;
  error?: IFieldValidation;
  renderField?: (name: string, onChange: (e: any) => void, value?: any) => any;
  datePickerOptions?: {
    constraints?: {
      min?: moment.Moment;
      max?: moment.Moment;
    };
    enableTimeZone?: boolean;
    timeFormat?: boolean | string;
    dateFormat?: boolean | string;
    timeConstraints?: TimeConstraints;
    placeholder?: string;
    formatAsDateOnly?: boolean; // CDS-6255: Format as YYYY-MM-DD without UTC conversion
  };
  handler?: JSX.Element;
  hideAfter?: number;
  selectAll?: boolean;
  search?: boolean;
  maxLength?: number;
  frequentAssigneesOptions?: any[];
  lastAssigneesOptions?: any[];
  multiLevelSearch: boolean;
}

export default class Input extends React.Component<IInputProps, {}> {
  static defaultProps = {
    name: '',
    width: 12,
    placeholder: '',
    onChange: () => null,
    onOptionChange: () => null,
    disabled: false,
    onBlur: () => null,
    onFocus: () => null,
    error: {
      errorState: IValidationState.SUCCESS,
      errorMessage: '',
    },
    datePickerOptions: {
      constraints: null,
      enableTimeZone: true,
    },
    renderField: null,
    multi: false,
    hideAfter: 10,
    selectAll: false,
    search: false,
    multiLevelSearch: false,
    className: '',
  };

  onKeyboardInputFocus = (e: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (this.props.onFocus) {
      this.props.onFocus(e);
    }
  };

  onKeyboardInputBlur = (e: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    this.props.onBlur(e);
  };

  getInputId = () => {
    const { name } = this.props;
    if (!name) {
      return undefined;
    }

    return `input-${name.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
  };

  supportsLabelFor = (fieldType: IField['type']) => {
    switch (fieldType) {
      case CustomFieldType.PICKLIST:
      case OtherFieldType.RADIOBUTTON:
      case OtherFieldType.PICKLIST_WITH_HANDLER:
      case OtherFieldType.FILTER:
        return false;
      default:
        return true;
    }
  };

  renderField = (inputId?: string, errorId?: string) => {
    const {
      name,
      field,
      placeholder,
      renderField,
      disabled,
      onChange,
      datePickerOptions,
      handler,
      maxLength,
      renderLabel,
      error,
    } = this.props;
    const ariaLabel =
      field?.label && field.label.trim().length ? field.label : placeholder && placeholder.trim().length ? placeholder : name;
    const hasLabel = Boolean(renderLabel) || Boolean(field?.label && field.label.trim().length);
    const inputAriaLabel = !hasLabel ? ariaLabel : undefined;
    const hasError = error?.errorState === IValidationState.ERROR || error?.errorState === IValidationState.WARNING;
    const ariaInvalid = hasError ? 'true' : 'false';
    const ariaDescribedBy = hasError && errorId ? errorId : undefined;
    if (renderField) {
      return renderField(name, onChange, field.value);
    } else {
      switch (field.type) {
        case CustomFieldType.PICKLIST:
          let newOptions: any = field.options;

          if (field.options && typeof field.options[0] === 'string') {
            newOptions = field.options.map((option) => ({
              value: option,
              label: option,
            }));
          }

          return (
            <Select
              name={name}
              value={field.displayValue ? field.displayValue : field.value}
              onChange={(event) => this.props.onChange(event)}
              onBlur={(event) => this.props.onBlur(event)}
              disabled={disabled}
              options={newOptions}
              placeholder={placeholder || 'Select Option'}
              onFocus={this.props.onFocus ? this.props.onFocus : undefined}
              className={this.props.className}
              ariaLabel={ariaLabel || 'Select Option'}
            />
          );

        case CustomFieldType.DATE:
          const updatedOptions = datePickerOptions ? datePickerOptions : {};

          // Parse the date value for proper display while avoiding timezone issues
          let dateValue = null;
          if (field.value) {
            // Check if the value is a valid date before parsing
            const momentDate = moment(field.value);
            if (momentDate.isValid()) {
              // Use moment to parse the date, which will handle ISO strings correctly
              // The key is not to convert to Date object which can cause timezone shifts
              dateValue = momentDate;
            }
          }

          return (
            <DatePicker
              name={name}
              onChange={(event) => this.props.onChange(event)}
              disabled={disabled}
              onBlur={this.props.onBlur}
              onFocus={this.props.onFocus}
              value={dateValue}
              required={field.isRequired}
              ariaLabel={ariaLabel || 'Select date'}
              inputId={inputId}
              {...updatedOptions}
            />
          );

        case CustomFieldType.TEXTAREA:
          return this.props.field.isResizeable ? (
            <div
              style={{
                minHeight: '250px',
              }}
            >
              <textarea
                id={inputId}
                name={name}
                onChange={(event) => this.props.onChange(event)}
                onBlur={this.onKeyboardInputBlur}
                onFocus={this.onKeyboardInputFocus}
                value={field.value ? field.value : ''}
                required={field.isRequired}
                aria-required={field.isRequired ? 'true' : 'false'}
                aria-invalid={ariaInvalid}
                aria-describedby={ariaDescribedBy}
                disabled={disabled}
                placeholder={placeholder}
                maxLength={maxLength}
                aria-label={inputAriaLabel}
                className={field.isResizeable ? 'resizeable-textarea' : ''}
              />
            </div>
          ) : (
            <textarea
              id={inputId}
              name={name}
              onChange={(event) => this.props.onChange(event)}
              onBlur={this.onKeyboardInputBlur}
              onFocus={this.onKeyboardInputFocus}
              value={field.value ? field.value : ''}
              required={field.isRequired}
              aria-required={field.isRequired ? 'true' : 'false'}
              aria-invalid={ariaInvalid}
              aria-describedby={ariaDescribedBy}
              disabled={disabled}
              placeholder={placeholder}
              maxLength={maxLength}
              aria-label={inputAriaLabel}
            />
          );

        case CustomFieldType.NUMBER:
          return (
            <input
              id={inputId}
              type={field.type}
              placeholder={placeholder ? placeholder : 'Enter Number'}
              name={name}
              onChange={(event) => this.props.onChange(event)}
              onBlur={this.onKeyboardInputBlur}
              onFocus={this.onKeyboardInputFocus}
              value={(field.value || field.value === 0) && !isNaN(field.value) ? field.value : ''}
              required={field.isRequired}
              aria-required={field.isRequired ? 'true' : 'false'}
              aria-invalid={ariaInvalid}
              aria-describedby={ariaDescribedBy}
              disabled={disabled}
              aria-label={inputAriaLabel}
            />
          );

        case CustomFieldType.PHONE:
          return (
            <PhoneInput
              id={inputId}
              placeholder={placeholder ? placeholder : 'Enter mobile number'}
              name={name}
              defaultCountry="US"
              onChange={debounce(
                (newValue) =>
                  this.props.onChange({
                    target: { name: this.props.name, value: newValue },
                    currentTarget: { name: this.props.name, value: newValue },
                  }),
                500
              )}
              onBlur={this.onKeyboardInputBlur}
              onFocus={this.onKeyboardInputFocus}
              value={field.value ? field.value : ''}
              required={field.isRequired}
              aria-required={field.isRequired ? 'true' : 'false'}
              aria-invalid={ariaInvalid}
              aria-describedby={ariaDescribedBy}
              disabled={disabled}
              aria-label={inputAriaLabel}
            />
          );

        case OtherFieldType.RADIOBUTTON:
          return (
            <RadioButton
              onChange={this.props.onChange}
              onFocus={(e) => this.props.onFocus(e)}
              value={field.value}
              name={name}
              options={this.props.valueLabelOptions}
              disabled={disabled}
              hideAfter={this.props.hideAfter}
              primaryColor={field ? field.primaryColor : ButtonStyle.PRIMARY}
              secondaryColor={field ? field.secondaryColor : ButtonStyle.DEFAULT}
              searchMode={this.props.search}
              multiLevelSearch={this.props.multiLevelSearch}
              frequentAssigneesOptions={this.props.frequentAssigneesOptions}
              lastAssigneesOptions={this.props.lastAssigneesOptions}
            />
          );

        case OtherFieldType.PICKLIST_WITH_HANDLER:
          let pickListOptions: any = field.options;

          if (field.options) {
            pickListOptions = field.options.map((option: IPickListOptions) => ({
              value: option.value,
              label: option.label,
              isChecked: option.isChecked || false,
              isDisabled: option.isDisabled || false,
            }));
          }

          return (
            <PicklistWithHandler
              name={this.props.name}
              value={field.value}
              options={pickListOptions}
              onChange={(event) => this.props.onChange(event)}
              onBlur={(event) => this.props.onBlur(event)}
              onFocus={this.props.onFocus ? this.props.onFocus : undefined}
              handler={handler}
              multi={field.multi}
              identifier={field.identifier}
              disabled={disabled}
              placeholder={placeholder || null}
              noRowsMessage={field.noRowsMessage}
              selectAll={this.props.selectAll}
              showHandler={field.showHandler}
              onOptionSelection={this.props.onOptionSelection}
              labelType={field.labelType}
            />
          );

        case OtherFieldType.FILTER:
          return (
            <Filter
              name={this.props.name}
              value={field.value}
              options={this.props.valueLabelOptions}
              onChange={(event) => this.props.onChange(event)}
              onBlur={(event) => this.props.onBlur(event)}
              onFocus={this.props.onFocus ? this.props.onFocus : undefined}
              placeholder={placeholder}
              disabled={disabled}
              handler={handler}
            />
          );

        default:
          return (
            <input
              id={inputId}
              type={field.type}
              placeholder={placeholder ? placeholder : 'Enter Text'}
              name={name}
              onChange={(event) => this.props.onChange(event)}
              onBlur={this.onKeyboardInputBlur}
              onFocus={this.onKeyboardInputFocus}
              value={field.value ? field.value : ''}
              required={field.isRequired}
              aria-required={field.isRequired ? 'true' : 'false'}
              aria-invalid={ariaInvalid}
              aria-describedby={ariaDescribedBy}
              disabled={disabled}
              aria-label={inputAriaLabel}
            />
          );
      }
    }
  };

  render() {
    const { width, widthMD, field, className } = this.props;
    const elementWidth = field.type === CustomFieldType.TEXTAREA ? 12 : width;
    const elementWidthMD = field.type === CustomFieldType.TEXTAREA ? 12 : widthMD;
    const { errorState, errorMessage } = this.props.error;
    let containerClassName =
      errorState === IValidationState.ERROR ? 'field-section field-section--error' : 'field-section';
    containerClassName = field.isRequired ? `${containerClassName} field-section--required` : containerClassName;
    const { renderLabel } = this.props;
    const inputId = this.getInputId();
    const labelFor = this.supportsLabelFor(field.type) ? inputId : undefined;
    const errorId = errorMessage ? `${inputId}-error` : undefined;

    return (
      <Col
        className={`${containerClassName} ${className ? className : ''}`}
        xs={elementWidth}
        md={elementWidthMD ? elementWidthMD : elementWidth}
      >
        {renderLabel && <Row className="field__label">{renderLabel()}</Row>}
        {field.label && (
          <Row className="field__label">
            <label className="field__label-label" title={field.label} htmlFor={labelFor}>
              {field.label}
            </label>
          </Row>
        )}
        <Row className="field__input">{this.renderField(inputId, errorId)}</Row>

        {(errorState === IValidationState.ERROR || errorState === IValidationState.WARNING) && (
          <span id={errorId} role="alert" aria-live="polite" className="field__error">
            {errorMessage}
          </span>
        )}
      </Col>
    );
  }
}
